import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { toQuotedString, createAllWorkUrl } from '@atlassian/jira-common-jql/src/helpers.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import type { accessCheck_softwareReleasesVersionDetail$key } from './__generated__/accessCheck_softwareReleasesVersionDetail.graphql';

const getVersionIssuesUrl = (versionName: string, projectKey: string) => {
	const jql = `project = ${toQuotedString(projectKey)} AND fixVersion = ${toQuotedString(
		versionName,
	)}`;
	return createAllWorkUrl(jql);
};

type Props = {
	jiraVersion: accessCheck_softwareReleasesVersionDetail$key;
	children: JSX.Element;
};

// If the user cannot view the version details page, this component will redirect them to a JQL search for the version's issues.
//
// It will be uncommon for this redirect to trigger in practice, as in most cases the link will either...
// - Not be shown at all if the conditions are not met (e.g. the Release Hub won't be accessible if the Releases feature is disabled).
// - Already point to the JQL search URL instead of this page (the issue view's fix version field does this for non-Software projects).
//
// This redirect is to catch the few remaining cases, e.g. the "fix version" issue field being visible even though the Releases feature is disabled (possible in Company Managed projects).
export const AccessCheck = ({ jiraVersion, children }: Props): React.JSX.Element => {
	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment accessCheck_softwareReleasesVersionDetail on JiraVersion {
				name @required(action: NONE)
				project @required(action: NONE) {
					key @required(action: NONE)
				}
				canViewVersionDetailsPage @required(action: NONE)
			}
		`,
		jiraVersion,
	);
	if (version != null && !version.canViewVersionDetailsPage) {
		return <Redirect to={getVersionIssuesUrl(version.name, version.project.key)} />;
	}
	return children;
};
